import { Injectable } from '@angular/core';
import { CURRENT_STATE_PROJECT_KEY, Linqset, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, createLinqsetNode, getInsightsWithoutCreate, getSuperType, getTypeDescription, getTypeName, isOrganisationNode, WORK_TYPE } from '../common/linqset-model';
import { MODEL_COLOURS } from './model-item-enums';
import { v4 } from 'uuid';
import { InformationAsset, modelChangedInProject, ModelDetails } from '../common/organisation';
import { LinqsetNodeVisualisation } from '../canvas/model/graph-model';
import { GraphComponent, IGraph, INode, Point, Rect } from '@yfiles/yfiles';
import { LinqNodeStyle } from '../canvas/styles/linq-node-style';
import { NodeStyleSettings, OUTPUT_SETTING } from '../ui-components/svgs/node-svgs';

export class NodeSuperTypeInfo {
    superType: NODE_SUPERTYPE = NODE_SUPERTYPE.ACTION
    name: string = ''
    colour: MODEL_COLOURS
    hoverColour: MODEL_COLOURS
    nodeTypes: NodeTypeInfo[] = []

    constructor(superType:NODE_SUPERTYPE, name:string, colour:MODEL_COLOURS, hover:MODEL_COLOURS) {
        this.superType = superType
        this.name = name
        this.colour = colour
        this.hoverColour = hover
    }

    getModelPalette(isOrganisationModel:boolean) {
        if (isOrganisationModel) {
            return this.nodeTypes.filter(n => isOrganisationNode(n.superType.superType))
        }else {
            return this.nodeTypes.filter(n => n.superType.superType !== NODE_SUPERTYPE.PEOPLE && n.superType.superType !== NODE_SUPERTYPE.INFRASTRUCTURE)
        }
    }
}

export interface NodeTypeInfo {
    superType: NodeSuperTypeInfo,
    type: NODE_TYPE,
    name: string,
    description: string,
    mainModel: boolean, // whether is included as an option on main model
}

@Injectable({
    providedIn: 'root'
})
export class NodeService {
    public readonly NODE_SIZE = 35;

    nodeSuperTypes: NodeSuperTypeInfo[] = []
    private nodeTypeMap = new Map<NODE_TYPE, NodeTypeInfo>()
    // types a model can hold that aren't offered in the palette (older types such as Outcome), described on first use
    private otherTypeMap = new Map<NODE_TYPE, NodeTypeInfo>()

    constructor() {
        const info = new NodeSuperTypeInfo(NODE_SUPERTYPE.INFORMATION, 'Information', MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR)
        info.nodeTypes.push(this.createNodeType(info, NODE_TYPE.INFORMATION))
        info.nodeTypes.push(this.createNodeType(info, NODE_TYPE.APPLICATION))
        info.nodeTypes.push(this.createNodeType(info, NODE_TYPE.SERVICE))
        info.nodeTypes.push(this.createNodeType(info, NODE_TYPE.DATABASE))
        info.nodeTypes.push(this.createNodeType(info, NODE_TYPE.FILE))
        info.nodeTypes.push(this.createNodeType(info, NODE_TYPE.PAPER))
        info.nodeTypes.push(this.createNodeType(info, NODE_TYPE.VERBAL))
        this.nodeSuperTypes.push(info)

        const action = new NodeSuperTypeInfo(NODE_SUPERTYPE.ACTION, 'Action', MODEL_COLOURS.ACTION_COLOR, MODEL_COLOURS.ACTION_HOVER_COLOR)
        action.nodeTypes.push(this.createNodeType(action, NODE_TYPE.ACTION))
        action.nodeTypes.push(this.createNodeType(action, NODE_TYPE.CAPTURE))
        action.nodeTypes.push(this.createNodeType(action, NODE_TYPE.PROCESS))
        action.nodeTypes.push(this.createNodeType(action, NODE_TYPE.SOMETHING_HAPPENS_HERE))
        this.nodeSuperTypes.push(action)

        const system = new NodeSuperTypeInfo(NODE_SUPERTYPE.SYSTEM, 'System', MODEL_COLOURS.SYSTEM_COLOR, MODEL_COLOURS.SYSTEM_HOVER_COLOR)
        system.nodeTypes.push(this.createNodeType(system, NODE_TYPE.SYSTEM))
        system.nodeTypes.push(this.createNodeType(system, NODE_TYPE.AI_AGENT))

        this.nodeSuperTypes.push(system)

        const roles = new NodeSuperTypeInfo(NODE_SUPERTYPE.ROLE, 'Role', MODEL_COLOURS.ROLE_COLOR, MODEL_COLOURS.ROLE_HOVER_COLOR)
        roles.nodeTypes.push(this.createNodeType(roles, NODE_TYPE.ROLE))
        roles.nodeTypes.push(this.createNodeType(roles, NODE_TYPE.EXTERNAL_ORGANISATION))
        this.nodeSuperTypes.push(roles)

        const people = new NodeSuperTypeInfo(NODE_SUPERTYPE.PEOPLE, 'People', MODEL_COLOURS.PEOPLE_COLOR, MODEL_COLOURS.PEOPLE_HOVER_COLOR)
        people.nodeTypes.push(this.createNodeType(people, NODE_TYPE.PERSON, false))
        people.nodeTypes.push(this.createNodeType(people, NODE_TYPE.TEAM, false))
        this.nodeSuperTypes.push(people)

        const infrastructure = new NodeSuperTypeInfo(NODE_SUPERTYPE.INFRASTRUCTURE, 'Infrastructure', MODEL_COLOURS.INFRASTRUCTURE_COLOR, MODEL_COLOURS.INFRASTRUCTURE_HOVER_COLOR)
        infrastructure.nodeTypes.push(this.createNodeType(infrastructure, NODE_TYPE.INFRASTRUCTURE, false))
        infrastructure.nodeTypes.push(this.createNodeType(infrastructure, NODE_TYPE.ON_PREMISE, false))
        infrastructure.nodeTypes.push(this.createNodeType(infrastructure, NODE_TYPE.CLOUD_BASED, false))
        this.nodeSuperTypes.push(infrastructure)

        const notes = new NodeSuperTypeInfo(NODE_SUPERTYPE.NOTES, 'Notes', MODEL_COLOURS.NOTES_COLOR, MODEL_COLOURS.NOTES_HOVER_COLOR)
        notes.nodeTypes.push(this.createNodeType(notes, NODE_TYPE.COMMENT))
        notes.nodeTypes.push(this.createNodeType(notes, NODE_TYPE.TODO))
        this.nodeSuperTypes.push(notes)

        const outcomes = new NodeSuperTypeInfo(NODE_SUPERTYPE.OUTCOMES, 'Outcomes', MODEL_COLOURS.OUTCOMES_COLOR, MODEL_COLOURS.OUTCOMES_HOVER_COLOR)
        outcomes.nodeTypes.push(this.createNodeType(outcomes, NODE_TYPE.OUTCOMES))
        this.nodeSuperTypes.push(outcomes)
    }

    createLinqsetNode(type:NODE_TYPE, linqset:Linqset):LinqsetNode {
        const superType = this.nodeTypeMap.get(type)?.superType.superType ?? getSuperType(type)
        return createLinqsetNode(v4(), superType, type, linqset)
    }

    createNewNodeAt(graph:IGraph, type:NODE_TYPE, linqset:Linqset, location:Point):INode {
        return this.createNodeAt(linqset, graph, this.createLinqsetNode(type, linqset), v4(), location)
    }

    createAssetNode(asset:InformationAsset, graphComponent:GraphComponent):INode {
        const settings = new NodeStyleSettings()
        settings.type = NODE_TYPE.ASSET

        return graphComponent.graph.createNode({
            layout: new Rect(0, 0, this.NODE_SIZE, this.NODE_SIZE),
            style: new LinqNodeStyle(settings),
            tag: {asset}
        });
    }

    createModelNodeSettings(model:ModelDetails, workspaceKey:string= CURRENT_STATE_PROJECT_KEY) {
        const settings = new NodeStyleSettings()
        settings.type = NODE_TYPE.MODEL
        if (workspaceKey !== CURRENT_STATE_PROJECT_KEY) {
            settings.projectNew = model.projectKey !== CURRENT_STATE_PROJECT_KEY
            settings.projectChanged = !settings.projectNew && modelChangedInProject(model, workspaceKey)
        }
        return settings
    }

    createModelNode(model:ModelDetails, workspaceKey:string, graphComponent:GraphComponent, location?:Point):INode {

        const settings = this.createModelNodeSettings(model, workspaceKey)

        if (location) {
            return graphComponent.graph.createNodeAt({
                location,
                style: new LinqNodeStyle(settings),
                tag: {model:model}
            });
        }else {
            return graphComponent.graph.createNode({
                layout: new Rect(0, 0, this.NODE_SIZE, this.NODE_SIZE),
                style: new LinqNodeStyle(settings),
                tag: {model:model}
            });
        }
    }

    createNodeForVisualisation(linqset:Linqset, visualisation:LinqsetNodeVisualisation, graph: IGraph):INode {
		const newNode = graph.createNode({
			layout: new Rect(0, 0, this.NODE_SIZE, this.NODE_SIZE),
			style: this.createNodeStyle(visualisation.node, linqset),
			tag: visualisation
        });

        return newNode
    }

    createNode(linqset:Linqset, node:LinqsetNode, visualisationKey:string, graph: IGraph, exclude?:boolean):INode {
        const visualisation = new LinqsetNodeVisualisation(node, visualisationKey)
        if (exclude) visualisation.excludeVisualisation()
        return this.createNodeForVisualisation(linqset, visualisation, graph)
    }

    createNodeAt(linqset:Linqset, graph: IGraph, node:LinqsetNode, visualisationKey:string, location:Point):INode {
        const visualisation = new LinqsetNodeVisualisation(node, visualisationKey)
		return graph.createNodeAt({
                location,
                style: this.createNodeStyle(visualisation.node, linqset),
                tag: visualisation
            })
    }

    setNodeStyle(graph:IGraph, node:INode, linqset:Linqset) {
        if (node?.tag?.node) graph.setStyle(node, this.createNodeStyle(node.tag.node, linqset))
    }

    private createNodeStyle(node:LinqsetNode, linqset:Linqset) {
        const i = linqset && getInsightsWithoutCreate(node, linqset)
        // an output is marked as without value only when nothing values or uses it: no value, no revenue and no derived
        // value (a dead end, as friction counts it)
        const output = i?.isOutput?((i?.revenue>0||i?.value>0||i?.derivedValue>0)?OUTPUT_SETTING.VALUE:OUTPUT_SETTING.NOVALUE):OUTPUT_SETTING.NONE
        const settings = new NodeStyleSettings(node, output)
        settings.waiting = node.superType === NODE_SUPERTYPE.ACTION && i?.workKind === WORK_TYPE.WAITING
        return new LinqNodeStyle(settings)
    }

    private createNodeType(superType:NodeSuperTypeInfo, type:NODE_TYPE, mainModel:boolean=true): NodeTypeInfo {
        const nodeType:NodeTypeInfo = {
            superType: superType,
            type: type,
            name: getTypeName(type),
            description: getTypeDescription(type),
            mainModel: mainModel
        }

        this.nodeTypeMap.set(type, nodeType)
        return nodeType;
    }

    public getType(node:LinqsetNode): NodeTypeInfo | undefined {
        return node && this.getNodeType(node.type)
    }

    // always describes a node type, so callers can read its name and colours: a type not offered in the palette
    // (Outcome, still found in older models) takes its super type's colours rather than breaking insights and downloads
    public getNodeType(type:NODE_TYPE): NodeTypeInfo | undefined {
        const known = this.nodeTypeMap.get(type)
        if (known || type === undefined || type === null) return known

        let other = this.otherTypeMap.get(type)
        if (!other) {
            const superType = this.getSuperTypeInfo(getSuperType(type))
            if (!superType) return undefined
            other = { superType, type, name: getTypeName(type), description: getTypeDescription(type), mainModel: false }
            this.otherTypeMap.set(type, other)
        }
        return other
    }

    public getSuperTypeInfo(superType:NODE_SUPERTYPE) {
        return this.nodeSuperTypes.find(s => s.superType === superType)
    }

    public getNodeTypeForSuperType(superType:NODE_SUPERTYPE): NodeTypeInfo {
        const superTypeinfo = this.getSuperTypeInfo(superType)
        return superTypeinfo && superTypeinfo.nodeTypes[0]
    }
}
